import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="problem-reports--storage-unit-selection"
export default class extends Controller {
    static targets = ["checkbox", "modal", "searchInput", "lockerList", "lockerItem", "selectionCount"]

    connect() {
        this.modalSelections = new Set()
        this._loadCurrentSelections()
    }

    syncModalWithSelections() {
        this._loadCurrentSelections()

        // Update checkboxes to match current selections
        this.checkboxTargets.forEach(checkbox => {
            const key = `${checkbox.dataset.storageUnitType}-${checkbox.dataset.storageUnitId}`
            checkbox.checked = this.modalSelections.has(key)
        })

        this._updateSelectionCount()
    }

    checkboxChanged(event) {
        const checkbox = event.target
        const key = `${checkbox.dataset.storageUnitType}-${checkbox.dataset.storageUnitId}`

        if (checkbox.checked) {
            this.modalSelections.add(key)
        } else {
            this.modalSelections.delete(key)
        }

        this._updateSelectionCount()
    }

    toggleLocker(event) {
        // Don't toggle if the click was directly on the checkbox
        if (event.target.type === 'checkbox') return
        const checkbox = event.currentTarget.querySelector('input[type="checkbox"]')
        if (checkbox) {
            checkbox.checked = !checkbox.checked
            checkbox.dispatchEvent(new Event('change', { bubbles: true }))
        }
    }

    filterLockers() {
        const query = this.searchInputTarget.value.toLowerCase().trim()
        this.lockerItemTargets.forEach(item => {
            const name = item.dataset.lockerName || ''
            item.style.display = name.includes(query) ? '' : 'none'
        })
    }

    confirmSelection() {
        this._updateNestedFields()
        this._closeModal()
    }

    cancelSelection() {
        this._loadCurrentSelections()
        this.syncModalWithSelections()
        this._closeModal()
    }

    _closeModal() {
        if (this.hasModalTarget) {
            this.modalTarget.close()
        }
    }

    _updateSelectionCount() {
        if (this.hasSelectionCountTarget) {
            this.selectionCountTarget.textContent = this.modalSelections.size
        }
    }

    _loadCurrentSelections() {
        this.modalSelections.clear()
        this.existingRecordIds = new Map() // storageKey -> recordId

        const container = this.element.querySelector('#storage_units_fields')
        if (!container) return

        const inputs = container.querySelectorAll('input[type="hidden"]')
        if (inputs.length === 0) return

        const storageUnits = new Map()

        inputs.forEach(input => {
            const match = input.name.match(/\[(\d+)\]\[(\w+)\]$/)
            if (match) {
                const [, index, fieldName] = match
                if (!storageUnits.has(index)) {
                    storageUnits.set(index, {})
                }
                storageUnits.get(index)[fieldName] = input.value
            }
        })

        // Process each complete record
        storageUnits.forEach(unit => {
            let storageKey = null
            if (unit.latch_id) {
                storageKey = `latch-${unit.latch_id}`
                this.modalSelections.add(storageKey)
            } else if (unit.g7_space_id) {
                storageKey = `g7_space-${unit.g7_space_id}`
                this.modalSelections.add(storageKey)
            } else if (unit.bike_hangar_id) {
                storageKey = `bike_hangar-${unit.bike_hangar_id}`
                this.modalSelections.add(storageKey)
            }
            // locker_number (other) entries from existing records are preserved as-is in the form

            // Store the record ID if this is an existing record
            if (storageKey && unit.id) {
                this.existingRecordIds.set(storageKey, unit.id)
            }
        })
    }

    _updateNestedFields() {
        const container = this.element.querySelector('#storage_units_fields')
        if (!container) return

        // Save any existing destruction fields before clearing
        const destructionFields = Array.from(container.querySelectorAll('input[name*="[_destroy]"][value="true"]'))
            .map(destroyInput => {
                const idInput = container.querySelector(`input[name="${destroyInput.name.replace('[_destroy]', '[id]')}"]`)
                return { destroyInput: destroyInput.cloneNode(), idInput: idInput?.cloneNode() }
            })
            .filter(pair => pair.idInput)

        container.replaceChildren()

        let index = 0

        // First, mark any existing records that are no longer selected for destruction
        if (this.existingRecordIds) {
            this.existingRecordIds.forEach((recordId, key) => {
                if (!this.modalSelections.has(key)) {
                    const fieldNamePrefix = `trouble_ticket[trouble_tickets_storage_units_attributes][${index}]`
                    const destroyDiv = document.createElement('div')

                    const idInput = document.createElement('input')
                    idInput.type = 'hidden'
                    idInput.name = `${fieldNamePrefix}[id]`
                    idInput.value = recordId

                    const destroyInput = document.createElement('input')
                    destroyInput.type = 'hidden'
                    destroyInput.name = `${fieldNamePrefix}[_destroy]`
                    destroyInput.value = 'true'

                    destroyDiv.append(idInput, destroyInput)
                    container.appendChild(destroyDiv)
                    index++
                }
            })
        }

        // Then create fields for current selections
        this.modalSelections.forEach(key => {
            const [type, id] = key.split('-', 2)
            const fieldNamePrefix = `trouble_ticket[trouble_tickets_storage_units_attributes][${index}]`
            const recordDiv = document.createElement('div')

            // Include existing record ID if this is an existing record
            const existingId = this.existingRecordIds?.get(key)
            if (existingId) {
                const idInput = document.createElement('input')
                idInput.type = 'hidden'
                idInput.name = `${fieldNamePrefix}[id]`
                idInput.value = existingId
                recordDiv.appendChild(idInput)
            }

            const typeInput = document.createElement('input')
            typeInput.type = 'hidden'
            typeInput.value = id

            if (type === 'latch') {
                typeInput.name = `${fieldNamePrefix}[latch_id]`
            } else if (type === 'g7_space') {
                typeInput.name = `${fieldNamePrefix}[g7_space_id]`
            } else if (type === 'bike_hangar') {
                typeInput.name = `${fieldNamePrefix}[bike_hangar_id]`
            }
            recordDiv.appendChild(typeInput)

            const displayDiv = document.createElement('div')
            const name = this._getStorageUnitName(type, id)
            const linkPath = this._getStorageUnitLinkPath(type, id)

            if (linkPath) {
                const link = document.createElement('a')
                link.href = linkPath
                link.target = '_blank'
                link.textContent = name
                displayDiv.appendChild(link)
            } else {
                displayDiv.textContent = name
            }

            recordDiv.appendChild(displayDiv)
            container.appendChild(recordDiv)
            index++
        })

        // Re-add any destruction fields that were saved earlier, using fresh indices
        // to avoid collisions with the destruction fields created above
        destructionFields.forEach(pair => {
            const fieldNamePrefix = `trouble_ticket[trouble_tickets_storage_units_attributes][${index}]`
            pair.idInput.name = `${fieldNamePrefix}[id]`
            pair.destroyInput.name = `${fieldNamePrefix}[_destroy]`
            const destroyDiv = document.createElement('div')
            destroyDiv.append(pair.idInput, pair.destroyInput)
            container.appendChild(destroyDiv)
            index++
        })
    }

    _getStorageUnitName(type, id) {
        const checkbox = this.checkboxTargets.find(cb =>
            cb.dataset.storageUnitType === type && cb.dataset.storageUnitId === id
        )
        return checkbox?.dataset.storageUnitName || id
    }

    _getStorageUnitLinkPath(type, id) {
        const checkbox = this.checkboxTargets.find(cb =>
            cb.dataset.storageUnitType === type && cb.dataset.storageUnitId === id
        )
        return checkbox?.dataset.storageUnitLinkPath || null
    }
}
